import moment from 'moment';
import React, { memo, useMemo } from 'react';
import styled from 'styled-components';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import usePaths from 'hooks/use-paths';
import useTranslation from 'hooks/use-translation';
import useUI from 'hooks/use-ui';
import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';
import {
  openInfoForPickupV2,
  acceptingAfterHoursForDelivery,
  acceptingAfterHoursForDeliveryV2,
  acceptingAfterHoursForPickup,
  acceptingAfterHoursForPickupV2,
} from 'shared/helpers/dispensaries';
import { openInfoForDispensary, openInfoForDispensaryV2 } from 'shared/core/helpers/dispensaries';
import InfoIcon from 'src/assets/info-icon';
import AccessibleLinkText from 'shared/components/accessible-link-text';
import { Link } from 'components/core';
import OpenInfoText from './open-info-text';

function StoreStatus({ dispensary, ...props }) {
  const { t } = useTranslation();
  const UI = useUI();
  const { href, route } = usePaths({ info: true });
  const isDesktopMenuSize = useMediaQuery((theme) => theme.breakpoints.up(`md`));
  const useOrderTypesConfigV2 = useDispensaryFlag(`rollout.use-updated-ordering-configurations`, dispensary?.id);
  const checkOnDuty = useDispensaryFlag(`dynamic-delivery-on-duty-check`, dispensary?.id);

  const MemoizedOpenInfoText = useMemo(() => {
    let openInfoForPickup;
    let openInfoForDelivery;
    let openInfo;

    if (useOrderTypesConfigV2) {
      openInfo = openInfoForDispensaryV2(dispensary, {});
      const { info } = openInfoForPickupV2(openInfo);
      openInfoForPickup = info;
      openInfoForDelivery = openInfo.delivery;
    } else {
      openInfo = openInfoForDispensary(dispensary, {});
      openInfoForPickup = openInfo.pickup;
      openInfoForDelivery = openInfo.delivery;
    }

    const { offerAnyPickupService, offerDelivery, status } = dispensary;
    const dispensaryIsActive = status === 'open';

    const offersPickupAndDelivery = offerAnyPickupService && offerDelivery;
    const onlyOffersPickup = offerAnyPickupService && !offerDelivery;
    const onlyOffersDelivery = offerDelivery && !offerAnyPickupService;

    const pickupReopenMoment = dispensaryIsActive ? openInfoForPickup.nextService.openMoment : null;
    const deliveryReopenMoment = dispensaryIsActive ? openInfoForDelivery.nextService.openMoment : null;
    const knownReopenMoment = pickupReopenMoment || deliveryReopenMoment;
    let reopenDate;

    if (offersPickupAndDelivery) {
      reopenDate = pickupReopenMoment?.isBefore(deliveryReopenMoment) ? pickupReopenMoment : deliveryReopenMoment;
    }

    if (onlyOffersPickup) {
      reopenDate = pickupReopenMoment;
    }

    if (onlyOffersDelivery) {
      reopenDate = deliveryReopenMoment;
    }
    if (!reopenDate) {
      return undefined;
    }
    const reopenHour = parseInt(reopenDate.format('H'), 10);
    const today = moment();
    const currentTime = parseInt(moment().format('H'), 10);
    // eslint-disable-next-line i18next/no-literal-string
    const tomorrow = today.add(1, 'days');
    // show the date only if we're past this point in time tomorrow or onwards
    const orderingReopens = t('common.orderingReopens', {
      defaultValue: 'Ordering reopens {{date}} at {{time}}',
      date: reopenDate.format(`M/DD`),
      time: reopenDate.format(`h:mma`).replace(':00', ''),
    });
    const orderingReopensToday = t('common.orderingReopensToday', {
      defaultValue: 'Ordering reopens at {{time}}',
      time: reopenDate.format(`h:mma`).replace(':00', ''),
    });
    /* eslint-enable i18next/no-literal-string */
    const nextOpensAt =
      reopenDate !== today && ((reopenDate === tomorrow && reopenHour > currentTime) || reopenDate.isAfter(tomorrow))
        ? orderingReopens
        : orderingReopensToday;

    const acceptAfterHoursDelivery = useOrderTypesConfigV2
      ? acceptingAfterHoursForDeliveryV2(dispensary)
      : acceptingAfterHoursForDelivery(dispensary);

    const acceptAfterHoursPickup = useOrderTypesConfigV2
      ? acceptingAfterHoursForPickupV2(dispensary)
      : acceptingAfterHoursForPickup(dispensary);

    let openForPickup = openInfoForPickup.isOpen;
    let openForDelivery = openInfoForDelivery.isOpen;
    if (checkOnDuty) {
      openForPickup = openInfoForPickup.isOpen && dispensary?.temporalLocation?.onDuty !== false;
      openForDelivery = openInfoForDelivery.isOpen && dispensary?.temporalLocation?.onDuty !== false;
    }

    return (
      <OpenInfoText
        dispensaryIsActive={dispensaryIsActive}
        acceptingAfterHoursForDelivery={acceptAfterHoursDelivery}
        acceptingAfterHoursForPickup={acceptAfterHoursPickup}
        offersDelivery={offerDelivery}
        offersPickup={offerAnyPickupService}
        openForDelivery={openForDelivery}
        openForPickup={openForPickup}
        nextOpensAt={knownReopenMoment ? nextOpensAt : null}
        {...props}
      />
    );
  }, [dispensary, props, t]);

  const { omitInfoLink } = props;
  const showInfoLink = !UI.viewOnlyMode && isDesktopMenuSize && !omitInfoLink;

  return (
    <React.Fragment>
      {MemoizedOpenInfoText}
      {showInfoLink && (
        <Link route={route} href={href}>
          <Anchor href={href} route={route}>
            <AccessibleLinkText>{t('common.dispensaryInformation', 'Dispensary information')}</AccessibleLinkText>
            <InfoIcon />
          </Anchor>
        </Link>
      )}
    </React.Fragment>
  );
}

export default memo(StoreStatus);

const Anchor = styled.a`
  position: relative;
  left: 10px;
  margin-right: 7px;
  display: flex;
  justify-content: center;
`;
